/** @jsxImportSource @emotion/react */
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';

import { Box, Flex } from 'components/styled';
import Text from 'components/styled/Text';
import Divider from 'components/styled/Divider';

import { UniVariant } from 'theme';
import { formatCzechCrowns } from 'utils';

import { Backend } from 'api/endpoints';

import { EASMultilanguage } from 'utils/EASTypes';
import { LicenseTypeById } from 'utils/enumMaps';

type TDescription = Record<LicenseTypeById, EASMultilanguage[]>;

/* PRODUCTS DESCRIPTIONS */

const muniDescription: TDescription = {
	LICENSE_FREE: [
		{
			CZECH: 'Pro státní správu a neziskové organizace',
			ENGLISH: 'For State Administration and Non-Profit Organisations',
		},
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: "Employer's profile in the portal",
		},
		{
			CZECH: 'Možnost libovolné obměny inzerátů',
			ENGLISH: 'Posibility to change the advertisements',
		},
	],
	LICENSE_ADVERT: [
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: "Employer's profile in the portal",
		},
		{
			CZECH: 'Možnost libovolné obměny inzerátů',
			ENGLISH: 'Posibility to change the advertisements',
		},
	],
	LICENSE_DATABASE: [
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: "Employer's profile in the portal",
		},
		{
			CZECH: 'Možnost libovolné obměny inzerátů',
			ENGLISH: 'Posibility to change the advertisements',
		},
	],
	LICENSE_COMPLETE: [
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: "Employer's profile in the portal",
		},
		{
			CZECH: 'Možnost libovolné obměny inzerátů',
			ENGLISH: 'Posibility to change the advertisements',
		},
	],
	LICENSE_PREMIUM: [
		{
			CZECH: 'Pro státní správu a neziskové organizace',
			ENGLISH: 'For State Administration and Non-Profit Organisations',
		},
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: "Employer's profile in the portal",
		},
		{
			CZECH: 'Možnost libovolné obměny inzerátů',
			ENGLISH: 'Posibility to change the advertisements',
		},
	],
};

const plzenDescription: TDescription = {
	LICENSE_FREE: [
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: 'Company profile in the portal',
		},
		{
			CZECH:
				'Možnost zveřejnění neomezeného množství nabídek kvalifikačních prací pro studenty',
			ENGLISH:
				'Possibility of publishing an unlimited number of offers of qualification theses for students',
		},
	],
	LICENSE_ADVERT: [
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: 'Company profile in the portal',
		},
		{
			CZECH:
				'Možnost zveřejnění neomezeného množství nabídek kvalifikačních prací pro studenty',
			ENGLISH:
				'Possibility of publishing an unlimited number of offers of qualification theses for students',
		},
	],
	LICENSE_DATABASE: [
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: 'Company profile in the portal',
		},
		{
			CZECH:
				'Možnost zveřejnění neomezeného množství nabídek kvalifikačních prací pro studenty',
			ENGLISH:
				'Possibility of publishing an unlimited number of offers of qualification theses for students',
		},
	],
	LICENSE_COMPLETE: [
		{
			CZECH: 'Profil zaměstnavatele v portálu',
			ENGLISH: 'Company profile in the portal',
		},
		{
			CZECH:
				'Možnost zveřejnění neomezeného množství nabídek kvalifikačních prací pro studenty',
			ENGLISH:
				'Possibility of publishing an unlimited number of offers of qualification theses for students',
		},
	],

	LICENSE_PREMIUM: [],
};

const liberecDescription: TDescription = {
	LICENSE_FREE: [
		{
			CZECH: 'Profil na portálu JobTUL',
			ENGLISH: 'Profile on the JobTUL portal',
		},
		{
			CZECH: 'Závěrečné práce, neplacené praxe',
			ENGLISH: 'Final theses, unpaid internships',
		},
		{
			CZECH:
				'Není možné zveřejňovat pracovní nabídky ani vyhledávat v databázi uchazečů',
			ENGLISH:
				'It is not possible to publish job offers or search in the database of candidates',
		},
	],
	LICENSE_ADVERT: [
		{
			CZECH: 'Profil zaměstnavatele v portálu JobTUL aktivní na 365 dní',
			ENGLISH: "Employer's profile in the JobTUL portal active for 365 days",
		},
		{
			CZECH: 'Viditelnost inzerátu je 30 dní',
			ENGLISH: 'The advertisement is visible for 30 days',
		},
		{
			CZECH: 'Možnost libovolné obměny inzerátů',
			ENGLISH: 'Posibility to change the advertisements',
		},
		{
			CZECH: 'Není možné aktivní vyhledávání a odkrývání profilů',
			ENGLISH: 'Active search and detection of profiles is not possible',
		},
		{
			CZECH:
				'Možnost zveřejňovat nabídky neplacených praxí a závěrečných prací',
			ENGLISH:
				'The possibility of publishing vacancies for unpaid internships and final thesis topics',
		},
	],
	LICENSE_DATABASE: [
		{
			CZECH: 'Profil zaměstnavatele v portálu JobTUL aktivní na 365 dní',
			ENGLISH: "Employer's profile in the JobTUL portal active for 365 days",
		},
		{
			CZECH: 'Viditelnost inzerátu je 30 dní',
			ENGLISH: 'The advertisement is visible for 30 days',
		},
		{
			CZECH: 'Vyhledávání v databázi uchazeču',
			ENGLISH: 'Candidate database search',
		},
		{
			CZECH:
				'Možnost zveřejňovat nabídky neplacených praxí a závěrečných prací',
			ENGLISH:
				'The possibility of publishing vacancies for unpaid internships and final thesis topics',
		},
		{
			CZECH:
				'Pokud si znovu zakoupíte profil tak, že nedojde k jeho neplatnosti, přičtou se Vám zbylá odkrytí k novým',
			ENGLISH:
				'If you re-purchase the same profile package any unused searches will be carried over to the next membership',
		},
	],
	LICENSE_COMPLETE: [
		{
			CZECH: 'Profil zaměstnavatele v portálu JobTUL aktivní na 365 dní',
			ENGLISH: "Employer's profile in the JobTUL portal active for 365 days",
		},
		{
			CZECH: 'Viditelnost inzerátu je 30 dní',
			ENGLISH: 'The advertisement is visible for 30 days',
		},
		{
			CZECH: 'Vyhledávání v databázi uchazeču',
			ENGLISH: 'Candidate database search',
		},
		{
			CZECH:
				'Možnost zveřejňovat nabídky neplacených praxí a závěrečných prací',
			ENGLISH:
				'The possibility of publishing vacancies for unpaid internships and final thesis topics',
		},
		{
			CZECH:
				'Pokud si znovu zakoupíte profil tak, že nedojde k jeho neplatnosti, přičtou se Vám zbylá odkrytí k novým',
			ENGLISH:
				'If you re-purchase the same profile package any unused searches will be carried over to the next membership',
		},
	],
	LICENSE_PREMIUM: [],
};

const zlinDescription: TDescription = {
	LICENSE_FREE: [
		{
			CZECH: 'Zavedení Vašeho profilu na JOB Portal UTB',
			ENGLISH: 'Creating your profile in the TBU JOB Portal',
		},
		{
			CZECH: 'Zveřejňujte nabídky stáží/praxí zcela <strong>neomezeně</strong>',
			ENGLISH:
				'Post an <strong>unlimited number</strong> of internships/practises.',
		},
		{
			CZECH:
				'Zveřejňujte nabídky závěrečných prací zcela <strong>neomezeně</strong>',
			ENGLISH:
				'Post an <strong>unlimited number</strong> of topics to be dealt with within final theses.',
		},
	],
	// new names : STANDART
	LICENSE_ADVERT: [
		{
			CZECH: 'Zavedení Vašeho profilu na JOB Portal UTB',
			ENGLISH: 'Creating your profile in the TBU JOB Portal',
		},
		{
			CZECH: 'Zveřejňujte nabídky stáží/praxí zcela <strong>neomezeně</strong>',
			ENGLISH:
				'Post an <strong>unlimited number</strong> of internships/practises.',
		},
		{
			CZECH:
				'Zveřejňujte nabídky závěrečných prací zcela <strong>neomezeně</strong>',
			ENGLISH:
				'Post an <strong>unlimited number</strong> of topics to be dealt with within final theses.',
		},
		{
			CZECH:
				'Můžete vytvořit neomezený počet inzerátů a <strong>jeden</strong> zvolit jako aktivní',
			ENGLISH:
				'You can create an unlimited number of advertisements and choose <strong>one</strong> advertisement to be active.',
		},
		{
			CZECH:
				'Zařazení nabídek do přehledů pracovních příležitostí v <strong>direct mailu</strong> pro studenty UTB',
			ENGLISH:
				'Inclusion of vacancies in the list of job opportunities sent by <strong>email</strong> directly to TBU students.',
		},
		{
			CZECH:
				'Zařazení nabídek do přehledů pracovních příležitostí na <strong>sociálních sítích</strong> Job Centra UTB',
			ENGLISH:
				'Inclusion of vacancies in the list of job opportunities promoted via <strong>social network platforms</strong> of the TBU Job Centre.',
		},
	],
	// new names : ADVANCED
	LICENSE_DATABASE: [
		{
			CZECH: 'Zavedení Vašeho profilu na JOB Portal UTB',
			ENGLISH: 'Creating your profile in the TBU JOB Portal',
		},
		{
			CZECH: 'Zveřejňujte nabídky stáží/praxí zcela <strong>neomezeně</strong>',
			ENGLISH:
				'Post an <strong>unlimited number</strong> of internships/practises.',
		},
		{
			CZECH:
				'Zveřejňujte nabídky závěrečných prací zcela <strong>neomezeně</strong>',
			ENGLISH:
				'Post an <strong>unlimited number</strong> of topics to be dealt with within final theses.',
		},
		{
			CZECH:
				'<strong>Vyhledávejte v databázi</strong> uchazečů z řad studentů a absolventů UTB',
			ENGLISH:
				'<strong>Search through the database </strong> of candidates from among TBU students and graduates.',
		},
		{
			CZECH:
				'Můžete vytvořit neomezený počet inzerátů a <strong>dva</strong> zvolit jako aktivní.',
			ENGLISH:
				'You can create an unlimited number of advertisements and choose <strong>two</strong> advertisements to be active.',
		},
		{
			CZECH:
				'Zařazení nabídek do přehledů pracovních příležitostí v <strong>direct mailu</strong> pro studenty UTB',
			ENGLISH:
				'Inclusion of vacancies in the list of job opportunities sent by <strong>email</strong> directly to TBU students.',
		},
		{
			CZECH:
				'Zařazení nabídek do přehledů pracovních příležitostí na <strong>sociálních sítích</strong> Job Centra UTB',
			ENGLISH:
				'Inclusion of vacancies in the list of job opportunities promoted via <strong>social network platforms</strong> of the TBU Job Centre.',
		},
	],
	LICENSE_COMPLETE: [
		{
			CZECH: 'Zavedení Vašeho profilu na JOB Portal UTB',
			ENGLISH: 'Creating your profile in the TBU JOB Portal',
		},
		{
			CZECH: 'Zveřejňujte nabídky stáží/praxí zcela <strong>neomezeně</strong>',
			ENGLISH:
				'Post an <strong>unlimited number</strong> of internships/practises.',
		},
		{
			CZECH:
				'Zveřejňujte nabídky závěrečných prací zcela <strong>neomezeně</strong>',
			ENGLISH:
				'Post an <strong>unlimited number</strong> of topics to be dealt with within final theses.',
		},
		{
			CZECH:
				'<strong>Vyhledávejte v databázi</strong> uchazečů z řad studentů a absolventů UTB',
			ENGLISH:
				'<strong>Search the database</strong> of applicants from among UTB students and alumni',
		},
		{
			CZECH:
				'Zařazení nabídek do přehledů pracovních příležitostí v <strong>direct mailu</strong> pro studenty UTB',
			ENGLISH:
				'Inclusion of vacancies in the list of job opportunities sent by <strong>email</strong> directly to TBU students.',
		},
		{
			CZECH:
				'Zařazení nabídek do přehledů pracovních příležitostí na <strong>sociálních sítích</strong> Job Centra UTB',
			ENGLISH:
				'Inclusion of vacancies in the list of job opportunities promoted via <strong>social network platforms</strong> of the TBU Job Centre.',
		},
		{
			CZECH:
				'Zveřejnění <strong>konkrétní nabídky</strong> vlastního výběru na sociálních sítích Job Centra UTB <strong>jednou ročně</strong>',
			ENGLISH:
				'Promotion of the offer of your own choice through the social network platforms of the TBU Job Centre <strong>once a year</strong>.',
		},
	],
	LICENSE_PREMIUM: [],
};

export const descriptionData = {
	MUNI: muniDescription,
	PLZEN: plzenDescription,
	LIBEREC: liberecDescription,
	ZLIN: zlinDescription,
};

type Props = {
	product: Backend.Product;
	isLicense?: boolean;
	university?: UniVariant;
	licenseType: LicenseTypeById;
};

export const LiberecProductCustomContent: FC<Props> = ({
	product,
	isLicense,
	licenseType,
}) => {
	const { t, i18n } = useTranslation(
		'components/tiles/product_tab/products_custom_data/',
	);
	return isLicense ? (
		<Box
			width={1}
			css={css`
				min-height: 320px;
				@media (max-width: 1400px) {
					min-height: 300px;
				}
				@media (max-width: 1000px) {
					min-height: 300px;
				}
				@media (max-width: 850px) {
					min-height: unset;
				}
			`}
		>
			<Flex alignItems="center" flexDirection="column">
				<Text fontSize="lg" color="primary" fontWeight="bold">
					{t('goal')}
					<Divider as="span" display="block" mt={3} />
				</Text>
			</Flex>

			{licenseType === 'LICENSE_FREE' && (
				<Text
					css={css`
						height: unset;
						@media (min-width: 1400px) {
							height: 100px;
						}
					`}
				>
					{t('description_0')}
				</Text>
			)}
			{licenseType === 'LICENSE_ADVERT' && (
				<Text
					css={css`
						height: unset;
						@media (min-width: 1400px) {
							height: 100px;
						}
					`}
				>
					{t('description_1')}
				</Text>
			)}
			{licenseType === 'LICENSE_DATABASE' && (
				<Text
					css={css`
						height: unset;
						@media (min-width: 1400px) {
							height: 100px;
						}
					`}
				>
					{t('description_2')}
				</Text>
			)}
			{licenseType === 'LICENSE_COMPLETE' && (
				<Text
					css={css`
						height: unset;
						@media (min-width: 1400px) {
							height: 100px;
						}
					`}
				>
					{t('description_3')}
				</Text>
			)}

			<Flex flexGrow={1} />
			<Box textAlign="left" alignSelf="flex-end">
				<Flex>
					<Flex alignItems="flex-start" flexDirection="column" width={1 / 2}>
						<Text fontSize="md" color="primary" fontWeight="bold" mb={0}>
							{t('price_per_year')}
						</Text>
					</Flex>
					<Flex
						width={1 / 2}
						justifyContent="flex-end"
						flexDirection="column"
						textAlign="right"
					>
						<Text fontWeight="bolder" fontSize="md" color="secondary" mb={0}>
							{formatCzechCrowns(product.priceWithoutTax, i18n.language)}
						</Text>
						<Text fontSize={10} color="primary" mt={0}>
							{t('without_vat')}
						</Text>
					</Flex>
				</Flex>

				<Flex>
					<Flex alignItems="flex-start" flexDirection="column" width={1 / 2}>
						<Text fontSize="md" color="primary" fontWeight="bold" mb={0}>
							{t('special_offer')}
						</Text>
						<Text mt={0} fontSize={10}>
							{t('for_partners')}
						</Text>
					</Flex>
					<Flex
						width={1 / 2}
						justifyContent="flex-end"
						flexDirection="column"
						textAlign="right"
					>
						<Text fontWeight="bolder" fontSize="md" color="secondary" mb={0}>
							{licenseType === 'LICENSE_FREE' &&
								formatCzechCrowns(0, i18n.language)}
							{licenseType === 'LICENSE_ADVERT' &&
								formatCzechCrowns(1500, i18n.language)}
							{licenseType === 'LICENSE_DATABASE' &&
								formatCzechCrowns(5000, i18n.language)}
							{licenseType === 'LICENSE_COMPLETE' &&
								formatCzechCrowns(12000, i18n.language)}
						</Text>
						<Text fontSize={10} color="primary" mt={0}>
							{t('without_vat')}
						</Text>
					</Flex>
				</Flex>
			</Box>
		</Box>
	) : null;
};

export const CustomProductSections: FC<Props> = ({ university, ...p }) => {
	switch (university) {
		case 'LIBEREC':
			return <LiberecProductCustomContent {...p} />;
		default:
			return null;
	}
};
